import { FormEvent, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { EMailInput, createEmail, deleteEmail, fetchEmails } from "@/lib/api"
import { useToast } from "@/components/Toast"
import { SkeletonZeile } from "@/components/Skeleton"
import { EmptyState } from "@/components/EmptyState"
import { Button, FieldError, Input } from "@/components/ui"
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet"

const LEER: EMailInput = { email: "", labels: [] }

/** E-Mail-Verwaltung innerhalb der Organisation-Detailseite (Ninox `S`). */
export function EMailsSection({ organisationId }: { organisationId: string }) {
  const queryClient = useQueryClient()
  const toast = useToast()
  const [neu, setNeu] = useState<EMailInput>(LEER)
  const [zeigeNeu, setZeigeNeu] = useState(false)
  const [emailFehler, setEmailFehler] = useState<string | null>(null)
  const [loeschenId, setLoeschenId] = useState<string | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ["emails", "organisation", organisationId],
    queryFn: () => fetchEmails({ organisationId }),
  })

  const invalidieren = () =>
    queryClient.invalidateQueries({ queryKey: ["emails", "organisation", organisationId] })

  const anlegen = useMutation({
    mutationFn: (payload: EMailInput) => createEmail({ ...payload, organisationId }),
    onSuccess: () => {
      invalidieren()
      setNeu(LEER)
      setZeigeNeu(false)
      toast.show("E-Mail-Adresse gespeichert.", "success")
    },
    onError: () => toast.show("E-Mail-Adresse konnte nicht gespeichert werden.", "error"),
  })

  const loeschen = useMutation({
    mutationFn: (id: string) => deleteEmail(id),
    onSuccess: () => {
      invalidieren()
      setLoeschenId(null)
      toast.show("E-Mail-Adresse gelöscht.", "success")
    },
    onError: () => toast.show("E-Mail-Adresse konnte nicht gelöscht werden.", "error"),
  })

  function onSubmitNeu(e: FormEvent) {
    e.preventDefault()
    if (!neu.email.trim()) {
      setEmailFehler("E-Mail-Adresse ist erforderlich.")
      document.getElementById("email-neu")?.focus()
      return
    }
    setEmailFehler(null)
    anlegen.mutate(neu)
  }

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900">E-Mail-Adressen</h2>
        <button
          type="button"
          onClick={() => setZeigeNeu(true)}
          className="rounded-md border border-slate-300 px-3 py-1 text-sm hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
        >
          + E-Mail hinzufügen
        </button>
      </div>

      {isLoading && (
        <div className="space-y-2">
          <SkeletonZeile className="h-10 w-full" />
          <SkeletonZeile className="h-10 w-full" />
        </div>
      )}

      <ul className="space-y-2">
        {data?.map((e) => (
          <li
            key={e.id}
            className="flex items-center justify-between rounded-md border border-slate-100 p-3 text-sm"
          >
            <div>
              <p className="font-medium text-slate-900">{e.email}</p>
              {e.labels.length > 0 && (
                <p className="text-slate-500">{e.labels.join(", ")}</p>
              )}
            </div>
            {loeschenId === e.id ? (
              <span className="flex items-center gap-1.5 text-xs">
                <span className="text-slate-600">Wirklich löschen?</span>
                <button
                  type="button"
                  onClick={() => loeschen.mutate(e.id)}
                  disabled={loeschen.isPending}
                  className="rounded-md bg-red-600 px-2 py-1 text-white hover:bg-red-700 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
                >
                  Ja, löschen
                </button>
                <button
                  type="button"
                  onClick={() => setLoeschenId(null)}
                  className="rounded-md border border-slate-300 px-2 py-1 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
                >
                  Abbrechen
                </button>
              </span>
            ) : (
              <button
                type="button"
                onClick={() => setLoeschenId(e.id)}
                className="rounded-md border border-red-300 px-2 py-1 text-xs text-red-700 hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
              >
                Löschen
              </button>
            )}
          </li>
        ))}
      </ul>

      {!isLoading && (data?.length ?? 0) === 0 && !zeigeNeu && (
        <EmptyState
          text="Noch keine E-Mail-Adressen erfasst."
          aktion={{ label: "+ E-Mail hinzufügen", onClick: () => setZeigeNeu(true) }}
        />
      )}

      <Sheet open={zeigeNeu} onOpenChange={setZeigeNeu}>
        <SheetContent sheetKey="email-anlegen" className="w-full overflow-y-auto sm:max-w-md">
          <SheetHeader>
            <SheetTitle>Neue E-Mail-Adresse</SheetTitle>
            <SheetDescription>E-Mail-Adresse für diese Organisation erfassen.</SheetDescription>
          </SheetHeader>
          <form id="email-neu-form" onSubmit={onSubmitNeu} noValidate className="grid gap-4 py-4">
            <div className="space-y-1.5">
              <label htmlFor="email-neu" className="block text-xs font-medium text-slate-700">
                E-Mail-Adresse *
              </label>
              <Input
                id="email-neu"
                type="email"
                required
                placeholder="name@beispiel.de"
                value={neu.email}
                onChange={(e) => {
                  setNeu({ ...neu, email: e.target.value })
                  if (emailFehler) setEmailFehler(null)
                }}
                invalid={!!emailFehler}
                aria-describedby={emailFehler ? "email-neu-fehler" : undefined}
              />
              <FieldError id="email-neu-fehler">{emailFehler}</FieldError>
            </div>
          </form>
          <SheetFooter>
            <Button type="button" variant="secondary" onClick={() => setZeigeNeu(false)}>
              Abbrechen
            </Button>
            <Button type="submit" form="email-neu-form" disabled={anlegen.isPending}>
              {anlegen.isPending ? "Speichert…" : "E-Mail speichern"}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </section>
  )
}
